// AlignmentToolbar.jsx - File unificato con logica, icone e componente
import React from 'react';

// =============================================
// LOGICA DI ALLINEAMENTO E DISTRIBUZIONE
// =============================================
class AlignmentUtils {
  
  // Funzioni di allineamento
  static alignLeft(elements) {
    const minX = Math.min(...elements.map(el => el.x));
    return elements.map(el => ({ ...el, x: minX }));
  }

  static alignRight(elements) {
    const maxX = Math.max(...elements.map(el => el.x + el.width));
    return elements.map(el => ({ ...el, x: maxX - el.width }));
  }

  static alignTop(elements) {
    const minY = Math.min(...elements.map(el => el.y));
    return elements.map(el => ({ ...el, y: minY }));
  }

  static alignBottom(elements) {
    const maxY = Math.max(...elements.map(el => el.y + el.height));
    return elements.map(el => ({ ...el, y: maxY - el.height }));
  }

  static alignCenterVertical(elements) {
    const minY = Math.min(...elements.map(el => el.y));
    const maxY = Math.max(...elements.map(el => el.y + el.height));
    const centerY = (minY + maxY) / 2;
    return elements.map(el => ({ 
      ...el, 
      y: centerY - el.height / 2 
    }));
  }

  static alignCenterHorizontal(elements) {
    const minX = Math.min(...elements.map(el => el.x));
    const maxX = Math.max(...elements.map(el => el.x + el.width));
    const centerX = (minX + maxX) / 2;
    return elements.map(el => ({ 
      ...el, 
      x: centerX - el.width / 2 
    }));
  }

  // Funzioni di distribuzione
  static distributeHorizontally(elements) {
    if (elements.length < 3) return elements;

    // Ordina per posizione X
    const sorted = [...elements].sort((a, b) => a.x - b.x);
    const leftmost = sorted[0];
    const rightmost = sorted[sorted.length - 1];
    
    // Calcola lo spazio totale disponibile
    const totalSpace = (rightmost.x + rightmost.width) - leftmost.x;
    const totalElementsWidth = sorted.reduce((sum, el) => sum + el.width, 0);
    const availableSpace = totalSpace - totalElementsWidth;
    const gapSize = availableSpace / (sorted.length - 1);

    // Distribuisci gli elementi
    return elements.map(el => {
      const sortedIndex = sorted.findIndex(s => s.id === el.id);
      if (sortedIndex === 0) {
        return el; // Il primo rimane fermo
      } else if (sortedIndex === sorted.length - 1) {
        return el; // L'ultimo rimane fermo
      } else {
        // Calcola la nuova posizione
        let newX = leftmost.x;
        for (let i = 0; i < sortedIndex; i++) {
          newX += sorted[i].width + gapSize;
        }
        return { ...el, x: newX };
      }
    });
  }

  static distributeVertically(elements) {
    if (elements.length < 3) return elements;

    // Ordina per posizione Y
    const sorted = [...elements].sort((a, b) => a.y - b.y);
    const topmost = sorted[0];
    const bottommost = sorted[sorted.length - 1];
    
    // Calcola lo spazio totale disponibile
    const totalSpace = (bottommost.y + bottommost.height) - topmost.y;
    const totalElementsHeight = sorted.reduce((sum, el) => sum + el.height, 0);
    const availableSpace = totalSpace - totalElementsHeight;
    const gapSize = availableSpace / (sorted.length - 1);

    // Distribuisci gli elementi
    return elements.map(el => {
      const sortedIndex = sorted.findIndex(s => s.id === el.id);
      if (sortedIndex === 0) {
        return el; // Il primo rimane fermo
      } else if (sortedIndex === sorted.length - 1) {
        return el; // L'ultimo rimane fermo
      } else {
        // Calcola la nuova posizione
        let newY = topmost.y;
        for (let i = 0; i < sortedIndex; i++) {
          newY += sorted[i].height + gapSize;
        }
        return { ...el, y: newY };
      }
    });
  }
}

// =============================================
// ICONE SVG PER ALLINEAMENTO
// =============================================
const AlignmentIcons = {
  AlignLeft: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M3 3v18M7 7h10M7 12h14M7 17h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),

  AlignRight: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M21 3v18M7 7h10M3 12h14M9 17h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),

  AlignTop: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M3 3h18M7 7v10M12 7v14M17 7v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),

  AlignBottom: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M3 21h18M7 7v10M12 3v14M17 9v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),

  AlignCenterVertical: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M3 12h18M7 8v8M12 5v14M17 9v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),

  AlignCenterHorizontal: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M12 3v18M8 7h8M5 12h14M9 17h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),

  DistributeHorizontal: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <rect x="2" y="8" width="4" height="8" fill="currentColor" rx="1"/>
      <rect x="10" y="8" width="4" height="8" fill="currentColor" rx="1"/>
      <rect x="18" y="8" width="4" height="8" fill="currentColor" rx="1"/>
      <path d="M6 12h4M14 12h4" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
    </svg>
  ),

  DistributeVertical: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <rect x="8" y="2" width="8" height="4" fill="currentColor" rx="1"/>
      <rect x="8" y="10" width="8" height="4" fill="currentColor" rx="1"/>
      <rect x="8" y="18" width="8" height="4" fill="currentColor" rx="1"/>
      <path d="M12 6v4M12 14v4" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
    </svg>
  )
};

// =============================================
// COMPONENTE TOOLBAR DI ALLINEAMENTO
// =============================================
export const AlignmentToolbar = ({ selectedElements, onUpdateElements }) => {
  if (!selectedElements || selectedElements.length < 2) {
    return null;
  }

  const handleAlignment = (alignmentType) => {
    let alignedElements;
    
    switch (alignmentType) {
      case 'left':
        alignedElements = AlignmentUtils.alignLeft(selectedElements);
        break;
      case 'right':
        alignedElements = AlignmentUtils.alignRight(selectedElements);
        break;
      case 'top':
        alignedElements = AlignmentUtils.alignTop(selectedElements);
        break;
      case 'bottom':
        alignedElements = AlignmentUtils.alignBottom(selectedElements);
        break;
      case 'centerVertical':
        alignedElements = AlignmentUtils.alignCenterVertical(selectedElements);
        break;
      case 'centerHorizontal':
        alignedElements = AlignmentUtils.alignCenterHorizontal(selectedElements);
        break;
      case 'distributeHorizontal':
        alignedElements = AlignmentUtils.distributeHorizontally(selectedElements);
        break;
      case 'distributeVertical':
        alignedElements = AlignmentUtils.distributeVertically(selectedElements);
        break;
      default:
        return;
    }
    
    onUpdateElements(alignedElements);
  };

  const buttonClass = "p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors duration-200 flex items-center justify-center";
  const separatorClass = "w-px h-6 bg-gray-300 mx-1";

  return (
    <div className="alignment-toolbar-inline">
      {/* Gruppo Allineamento Orizzontale */}
      <div className="alignment-group">
        <button
          onClick={() => handleAlignment('left')}
          className={buttonClass}
          title="Allinea a sinistra"
        >
          <AlignmentIcons.AlignLeft />
        </button>
        
        <button
          onClick={() => handleAlignment('centerHorizontal')}
          className={buttonClass}
          title="Allinea al centro orizzontale"
        >
          <AlignmentIcons.AlignCenterHorizontal />
        </button>
        
        <button
          onClick={() => handleAlignment('right')}
          className={buttonClass}
          title="Allinea a destra"
        >
          <AlignmentIcons.AlignRight />
        </button>
      </div>

      <div className={separatorClass}></div>

      {/* Gruppo Allineamento Verticale */}
      <div className="flex items-center gap-1">
        <button
          onClick={() => handleAlignment('top')}
          className={buttonClass}
          title="Allinea in alto"
        >
          <AlignmentIcons.AlignTop />
        </button>
        
        <button
          onClick={() => handleAlignment('centerVertical')}
          className={buttonClass}
          title="Allinea al centro verticale"
        >
          <AlignmentIcons.AlignCenterVertical />
        </button>
        
        <button
          onClick={() => handleAlignment('bottom')}
          className={buttonClass}
          title="Allinea in basso"
        >
          <AlignmentIcons.AlignBottom />
        </button>
      </div>

      <div className={separatorClass}></div>

      {/* Gruppo Distribuzione */}
      <div className="flex items-center gap-1">
        <button
          onClick={() => handleAlignment('distributeHorizontal')}
          className={`${buttonClass} ${selectedElements.length < 3 ? 'opacity-50 cursor-not-allowed' : ''}`}
          title="Distribuisci orizzontalmente"
          disabled={selectedElements.length < 3}
        >
          <AlignmentIcons.DistributeHorizontal />
        </button>
        
        <button
          onClick={() => handleAlignment('distributeVertical')}
          className={`${buttonClass} ${selectedElements.length < 3 ? 'opacity-50 cursor-not-allowed' : ''}`}
          title="Distribuisci verticalmente"
          disabled={selectedElements.length < 3}
        >
          <AlignmentIcons.DistributeVertical />
        </button>
      </div>
    </div>
  );
};

export default AlignmentToolbar;